import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  CurrencyDollarIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TableCellsIcon,
  TruckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import AdvancedFilters, { FilterCriteria } from '../../components/vehicles/AdvancedFilters';
import CarsForSaleImportConfig from '../../components/vehicles/CarsForSaleImportConfig';
import ExportModal from '../../components/vehicles/ExportModal';
import MarketingSyndicator from '../../components/vehicles/MarketingSyndicator';
import PriceAdjustmentModal from '../../components/vehicles/PriceAdjustmentModal';
import PricingSummaryCardCompact from '../../components/vehicles/PricingSummaryCardCompact';
import PricingTrendCharts from '../../components/vehicles/PricingTrendCharts';
import SalesAnalyticsCard from '../../components/vehicles/SalesAnalyticsCard';
import StockingGuideDashboard from '../../components/vehicles/StockingGuideDashboard';
import VehicleModal from '../../components/vehicles/VehicleModal';
import { usePreferences } from '../../contexts/PreferencesContext';
import { apiService } from '../../services/api';
import { Vehicle } from '../../types';

interface FacebookPagePostResponse {
  vehicle_id: string;
  page_url?: string | null;
  title: string;
  post_text: string;
  images: string[];
}

interface FacebookAutoPostResponse {
  page_id: string;
  post_id?: string | null;
  post_url?: string | null;
  message: string;
  images: string[];
  status: string;
}

// Vehicle thumbnail component — uses pre-loaded images from list API to avoid per-vehicle requests
const VehicleThumbnail: React.FC<{
  vehicleId?: string;
  year: number;
  make: string;
  model: string;
  images?: string[];
}> = ({ vehicleId: _vehicleId, year, make, model, images }) => {
  const [imageError, setImageError] = useState(false);

  // Use the first image from the list API response (already includes vehicle_images + JSON fallback)
  const ensureHttps = (url: string) =>
    url.startsWith('http://') ? url.replace('http://', 'https://') : url;
  const primaryImage =
    images && images.length > 0
      ? ensureHttps(images[0].startsWith('http') ? images[0] : apiService.getFileUrl(images[0]))
      : null;

  // Generate fallback based on vehicle details
  const getFallbackContent = () => {
    const makeInitial = make?.charAt(0)?.toUpperCase() || '?';
    const modelInitial = model?.charAt(0)?.toUpperCase() || '?';
    const yearLastTwo = year?.toString().slice(-2) || '??';

    return (
      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex flex-col items-center justify-center rounded">
        <div className="text-blue-600 font-bold text-lg">
          {makeInitial}
          {modelInitial}
        </div>
        <div className="text-blue-500 text-xs font-medium">{yearLastTwo}</div>
        <div className="text-blue-400 text-xs mt-1">No Image</div>
      </div>
    );
  };

  if (!primaryImage || imageError) {
    return (
      <div className="w-16 h-12 bg-gray-100 rounded border border-gray-300 flex items-center justify-center">
        {getFallbackContent()}
      </div>
    );
  }

  return (
    <div className="relative w-16 h-12">
      <img
        src={primaryImage}
        alt={`${year} ${make} ${model}`}
        className="w-full h-full object-cover rounded transition-opacity duration-200"
        onError={() => {
          console.warn(`Failed to load image for ${year} ${make} ${model}:`, primaryImage);
          setImageError(true);
        }}
        loading="lazy"
      />
    </div>
  );
};

// Inventory list defaults to showing only Available vehicles (Sold hidden by
// default). Value matches the stored vehicle.status enum ("AVAILABLE").
const DEFAULT_INVENTORY_FILTERS: FilterCriteria = { status: 'AVAILABLE' };

const VehiclesPage: React.FC = () => {
  const navigate = useNavigate();
  const { compactTableView, setCompactTableView } = usePreferences();
  const [searchTerm, setSearchTerm] = useState('');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [priceAdjustmentModalOpen, setPriceAdjustmentModalOpen] = useState(false);
  const [vehicleToAdjust, setVehicleToAdjust] = useState<any>(null);
  const [facebookPostVehicle, setFacebookPostVehicle] = useState<Vehicle | null>(null);
  const [facebookPageUrl, setFacebookPageUrl] = useState('');
  const [facebookPost, setFacebookPost] = useState<FacebookPagePostResponse | null>(null);
  const [facebookPostError, setFacebookPostError] = useState<string | null>(null);
  const [facebookPostLoading, setFacebookPostLoading] = useState(false);
  const [facebookAutoPost, setFacebookAutoPost] = useState<FacebookAutoPostResponse | null>(null);
  const [facebookAutoPostError, setFacebookAutoPostError] = useState<string | null>(null);
  const [facebookAutoPostLoading, setFacebookAutoPostLoading] = useState(false);

  // New dashboard states
  const [activeTab, setActiveTab] = useState('inventory');
  const [pricingSummary, setPricingSummary] = useState<any[]>([]);
  const [inventoryMetrics, setInventoryMetrics] = useState<any>({
    summary: null,
    recommendations: [],
    turnoverAnalysis: [],
    profitOptimizations: [],
    demandForecasts: [],
  });
  const [stockingGuideDateRange, setStockingGuideDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: null,
    endDate: null,
  });
  const [marketingData, setMarketingData] = useState<any[]>([]);
  const [salesMetrics, setSalesMetrics] = useState<any>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Advanced filtering states
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  // Inventory defaults to Available status (Sold hidden by default; still
  // selectable via the Status filter / "All Statuses", and the Sold tab).
  const [activeFilters, setActiveFilters] = useState<FilterCriteria>(DEFAULT_INVENTORY_FILTERS);
  const [availableMakes, setAvailableMakes] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);

  // Export states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportDataType, setExportDataType] = useState<'vehicles' | 'pricing' | 'analytics'>(
    'vehicles'
  );

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<Vehicle[]>('/vehicles/');
      // Ensure response is an array
      const vehiclesList = Array.isArray(response) ? response : [];
      setVehicles(vehiclesList);
      setError(null);
    } catch (err: any) {
      setError('Failed to load vehicles');
      console.error('Error fetching vehicles:', err);
      setVehicles([]); // Ensure vehicles is always an array
    } finally {
      setLoading(false);
    }
  };

  // New dashboard data fetching functions
  const fetchDashboardData = useCallback(
    async (tabName: string) => {
      setDashboardLoading(true);
      try {
        switch (tabName) {
          case 'pricing':
            const pricing = await apiService.get('/vehicles/dashboard/pricing-summary');
            setPricingSummary(Array.isArray(pricing) ? pricing : []);
            break;
          case 'stocking':
            let turnoverUrl = '/stocking-guide/turnover-analysis/';
            const turnoverParams = new URLSearchParams();
            if (stockingGuideDateRange.startDate)
              turnoverParams.append('date_from', stockingGuideDateRange.startDate);
            if (stockingGuideDateRange.endDate)
              turnoverParams.append('date_to', stockingGuideDateRange.endDate);
            const turnoverQuery = turnoverParams.toString();
            if (turnoverQuery) turnoverUrl += `?${turnoverQuery}`;

            // Call the new comprehensive Stocking Guide endpoints
            const [summary, recommendations, turnover, profitOpt, demand] = await Promise.all([
              apiService.get('/stocking-guide/dashboard/summary').catch(() => null),
              apiService.get('/stocking-guide/recommendations/').catch(() => []),
              apiService.get(turnoverUrl).catch(() => []),
              apiService.get('/stocking-guide/profit-optimization/').catch(() => []),
              apiService.get('/stocking-guide/demand-forecasting/').catch(() => []),
            ]);

            setInventoryMetrics({
              summary: summary || null,
              recommendations: Array.isArray(recommendations) ? recommendations : [],
              turnoverAnalysis: Array.isArray(turnover) ? turnover : [],
              profitOptimizations: Array.isArray(profitOpt) ? profitOpt : [],
              demandForecasts: Array.isArray(demand) ? demand : [],
            });
            break;
          case 'marketing':
            const marketing = await apiService.get('/vehicles/dashboard/marketing-status');
            setMarketingData(Array.isArray(marketing) ? marketing : []);
            break;
          case 'analytics':
            const analytics = await apiService.get('/vehicles/dashboard/sales-profitability');
            setSalesMetrics(analytics || null);
            break;
        }
      } catch (error) {
        console.error(`Failed to fetch ${tabName} data:`, error);
        // Show user-friendly error message
        toast.error(`Failed to load ${tabName} data. Please try again.`, {
          duration: 4000,
        });
      } finally {
        setDashboardLoading(false);
      }
    },
    [stockingGuideDateRange]
  );

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab !== 'inventory') {
      fetchDashboardData(tab);
    }
  };

  const handleSyndicate = async (vehicleId: string, platforms: string[]) => {
    try {
      await apiService.post(`/marketplaces/blast/${vehicleId}`, {
        platforms,
        dry_run: false,
      });
      // Refresh marketing data
      if (activeTab === 'marketing') {
        fetchDashboardData('marketing');
      }
    } catch (error) {
      console.error('Syndication failed:', error);
      throw error;
    }
  };

  const handleAnalyticsPeriodChange = async (period: string) => {
    try {
      const analytics = await apiService.get(
        `/vehicles/dashboard/sales-profitability?period=${period}`
      );
      setSalesMetrics(analytics);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleGenerateFacebookPost = async () => {
    if (!facebookPostVehicle) return;

    setFacebookPostLoading(true);
    setFacebookPostError(null);

    try {
      const response = await apiService.post<FacebookPagePostResponse>(
        `/marketplaces/facebook/page-post/${facebookPostVehicle.id}`,
        { page_url: facebookPageUrl || null }
      );
      setFacebookPost(response);
    } catch (error) {
      setFacebookPostError('Unable to generate Facebook page post.');
    } finally {
      setFacebookPostLoading(false);
    }
  };

  const handleCopyFacebookPost = async () => {
    if (!facebookPost?.post_text) return;
    await navigator.clipboard.writeText(facebookPost.post_text);
    toast.success('Facebook post copied to clipboard.');
  };

  const handleAutoPostToFacebook = async () => {
    if (!facebookPostVehicle) return;

    setFacebookAutoPostLoading(true);
    setFacebookAutoPostError(null);

    try {
      const response = await apiService.post<FacebookAutoPostResponse>(
        `/marketplaces/facebook/pages/post/${facebookPostVehicle.id}`,
        {
          message: facebookPost?.post_text || undefined,
          use_ai: !facebookPost?.post_text,
        }
      );
      setFacebookAutoPost(response);
    } catch (error) {
      setFacebookAutoPostError('Unable to auto-post to Facebook. Ensure a page is connected.');
    } finally {
      setFacebookAutoPostLoading(false);
    }
  };

  const handleCloseFacebookPost = () => {
    setFacebookPostVehicle(null);
  };

  // Trigger stocking guide refresh when date range changes
  useEffect(() => {
    if (activeTab === 'stocking') {
      fetchDashboardData('stocking');
    }
  }, [activeTab, fetchDashboardData, stockingGuideDateRange]);

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    setFacebookPageUrl('');
    setFacebookPost(null);
    setFacebookPostError(null);
    setFacebookPostLoading(false);
    setFacebookAutoPost(null);
    setFacebookAutoPostError(null);
    setFacebookAutoPostLoading(false);
  }, [facebookPostVehicle]);

  // Extract available filter options from vehicles data
  useEffect(() => {
    if (vehicles && vehicles.length > 0) {
      const makes = Array.from(new Set(vehicles.map((v) => v.make).filter(Boolean))).sort();
      const models = Array.from(new Set(vehicles.map((v) => v.model).filter(Boolean))).sort();
      const statuses = Array.from(new Set(vehicles.map((v) => v.status).filter(Boolean))).sort();
      const locations = Array.from(
        new Set(vehicles.map((v) => v.location).filter((loc): loc is string => Boolean(loc)))
      ).sort();

      setAvailableMakes(makes);
      setAvailableModels(models);
      setAvailableStatuses(statuses);
      setAvailableLocations(locations);
    }
  }, [vehicles]);

  const handleApplyFilters = (filters: FilterCriteria) => {
    setActiveFilters(filters);
  };

  const handleClearFilters = () => {
    setActiveFilters(DEFAULT_INVENTORY_FILTERS);
  };

  const handleExport = async (
    format: 'csv' | 'excel' | 'pdf',
    selectedFields: string[],
    includeCharts?: boolean
  ) => {
    try {
      setActionLoading(true);

      let dataToExport;
      let filename;

      switch (exportDataType) {
        case 'vehicles':
          dataToExport = filteredVehicles;
          filename = `vehicles_export_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'pricing':
          dataToExport = pricingSummary;
          filename = `pricing_summary_${new Date().toISOString().split('T')[0]}`;
          break;
        case 'analytics':
          dataToExport = salesMetrics ? [salesMetrics] : [];
          filename = `analytics_report_${new Date().toISOString().split('T')[0]}`;
          break;
        default:
          throw new Error('Invalid export data type');
      }

      if (!dataToExport || dataToExport.length === 0) {
        toast.error('No data available to export');
        return;
      }

      // Create export payload
      const exportPayload = {
        format,
        fields: selectedFields,
        data: exportDataType, // 'vehicles', 'pricing', or 'analytics'
        includeCharts: includeCharts || false,
        filename,
      };

      // Call export API
      const response = await apiService.post('/vehicles/export', exportPayload, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Export completed successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddVehicle = () => {
    setSelectedVehicle(null);
    setModalOpen(true);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setModalOpen(true);
  };

  const handleSaveVehicle = async (
    vehicleData: Record<string, unknown>
  ): Promise<Vehicle | void> => {
    try {
      setActionLoading(true);

      if (selectedVehicle) {
        // Update existing vehicle
        // Filter out fields that are not accepted by the backend VehicleUpdate model
        // VIN should not be updated as it's an immutable identifier
        const { vin: _vin, ...filteredData } = vehicleData;

        // Remove undefined/null values to prevent validation errors
        const updateData: Record<string, unknown> = {};
        // BHPH numeric fields must be sent even when cleared (as null) so that
        // clearing a previously-set value persists — the backend uses
        // exclude_unset, so an omitted field keeps its old DB value.
        const BHPH_NULLABLE_FIELDS = [
          'bhph_min_down_value',
          'bhph_payment_amount',
          'bhph_term_months',
        ];
        Object.keys(filteredData).forEach((key) => {
          const value = filteredData[key];
          if (BHPH_NULLABLE_FIELDS.includes(key)) {
            updateData[key] =
              value === '' || value === undefined || value === null ? null : Number(value);
            return;
          }
          if (value !== undefined && value !== null && value !== '') {
            // Ensure numeric fields are actually numbers
            if (
              ['year', 'mileage', 'asking_price', 'purchase_price', 'market_value'].includes(key)
            ) {
              const numValue = Number(value);
              if (!isNaN(numValue)) {
                updateData[key] = numValue;
              }
            } else {
              updateData[key] = value;
            }
          }
        });

        const savedVehicle = await apiService.put<Vehicle>(
          `/vehicles/${selectedVehicle.id}`,
          updateData
        );
        setVehicles((prev) => prev.map((v) => (v.id === selectedVehicle.id ? savedVehicle : v)));
        setModalOpen(false);
        setSelectedVehicle(null);

        // Show success toast
        toast.success('Vehicle updated successfully!', {
          duration: 3000,
          icon: '✅',
        });

        return savedVehicle;
      } else {
        // Create new vehicle
        const savedVehicle = await apiService.post<Vehicle>('/vehicles/', vehicleData);
        setVehicles((prev) => [savedVehicle, ...prev]);
        // Don't close modal for new vehicles - let them add images
        setSelectedVehicle(savedVehicle);

        // Show success toast
        toast.success('Vehicle created successfully!', {
          duration: 3000,
          icon: '✅',
        });

        return savedVehicle;
      }
    } catch (error: unknown) {
      console.error('Error saving vehicle:', error);

      // Extract meaningful error message
      let errorMessage = 'Failed to save vehicle. Please try again.';

      const axiosError = error as {
        response?: { data?: { detail?: unknown; message?: string } };
        message?: string;
      };
      if (axiosError?.response?.data) {
        const data = axiosError.response.data;

        // Handle FastAPI validation errors
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail
            .map((err: { msg?: string }) =>
              typeof err === 'object' && err.msg ? err.msg : 'Validation error'
            )
            .join(', ');
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (axiosError?.message) {
        errorMessage = axiosError.message;
      }

      // Show error toast
      toast.error(errorMessage, {
        duration: 5000,
        icon: '❌',
      });

      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustPrice = (vehicle: {
    vehicle_id: string;
    vin: string;
    year: number;
    make: string;
    model: string;
    [key: string]: unknown;
  }) => {
    setVehicleToAdjust(vehicle);
    setPriceAdjustmentModalOpen(true);
  };

  const handleViewDetails = (vehicle: { vehicle_id?: string; id?: string }) => {
    navigate(`/vehicles/${vehicle.vehicle_id || vehicle.id}`);
  };

  const handlePriceUpdate = async (
    vehicleId: string,
    pricingData: {
      asking_price?: number;
      wholesale_value?: number;
      trade_in_value?: number;
      retail_value?: number;
      acquisition_cost?: number;
      total_cost?: number;
    }
  ) => {
    try {
      setActionLoading(true);

      // Update vehicle pricing via API
      await apiService.patch(`/vehicles/${vehicleId}`, pricingData);

      // Update the pricing summary data
      setPricingSummary((prev) =>
        prev.map((v) => (v.vehicle_id === vehicleId ? { ...v, ...pricingData } : v))
      );

      // Also update main vehicles list if present
      setVehicles((prev) => prev.map((v) => (v.id === vehicleId ? { ...v, ...pricingData } : v)));

      // Refresh dashboard data to get updated profit calculations
      await fetchDashboardData('pricing');

      toast.success('Pricing updated successfully');
    } catch (error) {
      console.error('Error updating vehicle pricing:', error);
      toast.error('Failed to update pricing');
      throw error;
    } finally {
      setActionLoading(false);
      setPriceAdjustmentModalOpen(false);
      setVehicleToAdjust(null);
    }
  };

  const filteredVehicles = (vehicles || []).filter((vehicle) => {
    // Text search (VIN, make, model, stock number, year)
    const matchesSearch =
      !searchTerm ||
      vehicle.vin.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.stock_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.year?.toString().includes(searchTerm);

    // Advanced filters
    const matchesMake = !activeFilters.make || vehicle.make === activeFilters.make;
    const matchesModel = !activeFilters.model || vehicle.model === activeFilters.model;
    const matchesStatus =
      !activeFilters.status || vehicle.status?.toLowerCase() === activeFilters.status.toLowerCase();
    const matchesLocation = !activeFilters.location || vehicle.location === activeFilters.location;

    const matchesYearMin =
      !activeFilters.yearMin || (vehicle.year && vehicle.year >= activeFilters.yearMin);
    const matchesYearMax =
      !activeFilters.yearMax || (vehicle.year && vehicle.year <= activeFilters.yearMax);

    const matchesPriceMin =
      !activeFilters.priceMin ||
      (vehicle.asking_price && vehicle.asking_price >= activeFilters.priceMin);
    const matchesPriceMax =
      !activeFilters.priceMax ||
      (vehicle.asking_price && vehicle.asking_price <= activeFilters.priceMax);

    const matchesMileageMin =
      !activeFilters.mileageMin || (vehicle.mileage && vehicle.mileage >= activeFilters.mileageMin);
    const matchesMileageMax =
      !activeFilters.mileageMax || (vehicle.mileage && vehicle.mileage <= activeFilters.mileageMax);

    const matchesDaysOnLotMin = !activeFilters.daysOnLotMin; // Not available in current Vehicle interface
    const matchesDaysOnLotMax = !activeFilters.daysOnLotMax; // Not available in current Vehicle interface

    const profit =
      vehicle.asking_price && vehicle.acquisition_cost
        ? vehicle.asking_price - vehicle.acquisition_cost
        : 0;
    const matchesProfitMin = !activeFilters.profitMin || profit >= activeFilters.profitMin;
    const matchesProfitMax = !activeFilters.profitMax || profit <= activeFilters.profitMax;

    return (
      matchesSearch &&
      matchesMake &&
      matchesModel &&
      matchesStatus &&
      matchesLocation &&
      matchesYearMin &&
      matchesYearMax &&
      matchesPriceMin &&
      matchesPriceMax &&
      matchesMileageMin &&
      matchesMileageMax &&
      matchesDaysOnLotMin &&
      matchesDaysOnLotMax &&
      matchesProfitMin &&
      matchesProfitMax
    );
  });

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">Inventory Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Comprehensive vehicle management with inventory, pricing, and analytics.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex gap-2">
          {activeTab === 'inventory' && (
            <>
              <button
                className="btn-secondary"
                onClick={() => {
                  setExportDataType('vehicles');
                  setExportModalOpen(true);
                }}
                disabled={loading}
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export
              </button>
              <button className="btn-secondary" onClick={fetchVehicles} disabled={loading}>
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </>
          )}
          {activeTab === 'pricing' && (
            <button
              className="btn-secondary"
              onClick={() => {
                setExportDataType('pricing');
                setExportModalOpen(true);
              }}
              disabled={dashboardLoading}
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export Pricing
            </button>
          )}
          {activeTab === 'analytics' && (
            <button
              className="btn-secondary"
              onClick={() => {
                setExportDataType('analytics');
                setExportModalOpen(true);
              }}
              disabled={dashboardLoading}
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export Report
            </button>
          )}
          <button className="btn-primary" onClick={handleAddVehicle}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Enhanced Dashboard Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('inventory')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'inventory'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TruckIcon className="w-5 h-5 inline mr-2" />
              Active
            </button>
            <button
              onClick={() => handleTabChange('sold')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sold'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ArchiveBoxIcon className="w-5 h-5 inline mr-2" />
              Sold
            </button>
            <button
              onClick={() => handleTabChange('pricing')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pricing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CurrencyDollarIcon className="w-5 h-5 inline mr-2" />
              Price Summary
            </button>
            <button
              onClick={() => handleTabChange('stocking')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stocking'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="w-5 h-5 inline mr-2" />
              Stocking Guide
            </button>
            <button
              onClick={() => handleTabChange('marketing')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'marketing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GlobeAltIcon className="w-5 h-5 inline mr-2" />
              Online Marketing
            </button>
            <button
              onClick={() => handleTabChange('analytics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="w-5 h-5 inline mr-2" />
              Sales & Profitability
            </button>
            <button
              onClick={() => handleTabChange('ftp-import')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'ftp-import'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CloudArrowUpIcon className="w-5 h-5 inline mr-2" />
              FTP Import
            </button>
          </nav>
        </div>
      </div>

      {/* Inventory Tab Content - PRESERVING EXISTING FUNCTIONALITY */}
      {activeTab === 'inventory' && (
        <>
          {/* Search and filters - Enhanced */}
          <div className="card mb-6">
            <div className="space-y-4">
              {/* Search bar */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 sm:col-span-8">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="form-input pl-10 w-full placeholder:text-sm"
                      placeholder="Search by VIN, make, model, stock number, or year..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-4 flex gap-2">
                  <button
                    className="btn-secondary flex-1 sm:flex-initial"
                    onClick={() => setAdvancedFiltersOpen(true)}
                    title="Open advanced filtering options"
                  >
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Advanced Filters</span>
                    <span className="sm:hidden">Filters</span>
                    {Object.keys(activeFilters).length > 0 && (
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                        {Object.keys(activeFilters).length}
                      </span>
                    )}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={fetchVehicles}
                    disabled={loading}
                    title="Refresh vehicle list"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    className={`btn-secondary hidden sm:flex items-center ${compactTableView ? 'bg-blue-100 border-blue-300' : ''}`}
                    onClick={() => setCompactTableView(!compactTableView)}
                    title={compactTableView ? 'Switch to normal view' : 'Switch to compact view'}
                  >
                    <TableCellsIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Active filters display */}
              {Object.keys(activeFilters).length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm text-gray-600">Active filters:</span>
                  {Object.entries(activeFilters).map(([key, value]) => {
                    if (!value) return null;
                    const label = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase());
                    return (
                      <span
                        key={key}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                      >
                        {label}: {value}
                        <button
                          onClick={() => {
                            const newFilters = { ...activeFilters };
                            delete newFilters[key as keyof FilterCriteria];
                            setActiveFilters(newFilters);
                          }}
                          className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                  <button
                    onClick={handleClearFilters}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* PRESERVED: Original Vehicle List in Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="card">
          {loading ? (
            <div className="text-center py-12">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading vehicles...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">
                <p className="text-lg font-medium">
                  {typeof error === 'string' ? error : 'Failed to load vehicles'}
                </p>
                <button className="btn-secondary mt-4" onClick={fetchVehicles}>
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Try Again
                </button>
              </div>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <PlusIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {vehicles.length === 0 ? 'No vehicles in inventory' : 'No matching vehicles'}
              </h3>
              <p className="text-gray-500 mb-6">
                {vehicles.length === 0
                  ? 'Get started by adding your first vehicle or importing your inventory.'
                  : 'Try adjusting your search criteria.'}
              </p>
              {vehicles.length === 0 && (
                <div className="flex justify-center gap-3">
                  <button className="btn-primary" onClick={handleAddVehicle}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Vehicle
                  </button>
                  <button className="btn-secondary">Import Inventory</button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-hidden">
              {/* Mobile Card View */}
              <div className="block md:hidden space-y-4">
                {filteredVehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                  >
                    <div className="flex items-start space-x-4">
                      <VehicleThumbnail
                        vehicleId={vehicle.id}
                        year={vehicle.year}
                        make={vehicle.make}
                        model={vehicle.model}
                        images={vehicle.images}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">VIN: {vehicle.vin}</p>
                            {vehicle.exterior_color && (
                              <p className="text-xs text-gray-500">{vehicle.exterior_color}</p>
                            )}
                          </div>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              vehicle.status === 'available'
                                ? 'bg-green-100 text-green-800'
                                : vehicle.status === 'sold'
                                  ? 'bg-blue-100 text-blue-800'
                                  : vehicle.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : vehicle.status === 'wholesale'
                                      ? 'bg-orange-100 text-orange-800'
                                      : vehicle.status === 'repo'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {vehicle.status}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Stock:</span>
                            <span className="ml-1 font-medium">{vehicle.stock_number}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Mileage:</span>
                            <span className="ml-1 font-medium">
                              {vehicle.mileage ? vehicle.mileage.toLocaleString() : 'N/A'}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500">Price:</span>
                            <span className="ml-1 font-medium text-green-600">
                              $
                              {vehicle.asking_price ? vehicle.asking_price.toLocaleString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex space-x-2">
                          <Link
                            to={`/vehicle/${vehicle.vin}`}
                            className="flex-1 text-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                          >
                            View
                          </Link>
                          <button
                            className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100"
                            onClick={() => handleEditVehicle(vehicle)}
                          >
                            Edit
                          </button>
                          <button
                            className="flex-1 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
                            onClick={() => setFacebookPostVehicle(vehicle)}
                          >
                            Facebook Post
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <table className="hidden md:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${compactTableView ? 'px-3 py-2' : 'px-6 py-3'}`}
                    >
                      Vehicle
                    </th>
                    <th
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${compactTableView ? 'px-3 py-2' : 'px-6 py-3'}`}
                    >
                      Stock #
                    </th>
                    <th
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${compactTableView ? 'px-3 py-2' : 'px-6 py-3'}`}
                    >
                      Mileage
                    </th>
                    <th
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${compactTableView ? 'px-3 py-2' : 'px-6 py-3'}`}
                    >
                      Price
                    </th>
                    <th
                      className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${compactTableView ? 'px-3 py-2' : 'px-6 py-3'}`}
                    >
                      Status
                    </th>
                    <th className={`relative ${compactTableView ? 'px-3 py-2' : 'px-6 py-3'}`}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-gray-50">
                      <td
                        className={`whitespace-nowrap ${compactTableView ? 'px-3 py-2' : 'px-6 py-4'}`}
                      >
                        <div className="flex items-center">
                          {!compactTableView && (
                            <div className="mr-4">
                              <VehicleThumbnail
                                vehicleId={vehicle.id}
                                year={vehicle.year}
                                make={vehicle.make}
                                model={vehicle.model}
                                images={vehicle.images}
                              />
                            </div>
                          )}
                          <div>
                            <div
                              className={`font-medium text-gray-900 ${compactTableView ? 'text-xs' : 'text-sm'}`}
                            >
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </div>
                            <div
                              className={`text-gray-500 ${compactTableView ? 'text-xs' : 'text-sm'}`}
                            >
                              VIN: {vehicle.vin}
                            </div>
                            {!compactTableView && vehicle.exterior_color && (
                              <div className="text-xs text-gray-500">{vehicle.exterior_color}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td
                        className={`whitespace-nowrap text-gray-900 ${compactTableView ? 'px-3 py-2 text-xs' : 'px-6 py-4 text-sm'}`}
                      >
                        {vehicle.stock_number}
                      </td>
                      <td
                        className={`whitespace-nowrap text-gray-900 ${compactTableView ? 'px-3 py-2 text-xs' : 'px-6 py-4 text-sm'}`}
                      >
                        {vehicle.mileage ? vehicle.mileage.toLocaleString() : 'N/A'}
                      </td>
                      <td
                        className={`whitespace-nowrap text-gray-900 ${compactTableView ? 'px-3 py-2 text-xs' : 'px-6 py-4 text-sm'}`}
                      >
                        ${vehicle.asking_price ? vehicle.asking_price.toLocaleString() : 'N/A'}
                      </td>
                      <td
                        className={`whitespace-nowrap ${compactTableView ? 'px-3 py-2' : 'px-6 py-4'}`}
                      >
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            vehicle.status === 'available'
                              ? 'bg-green-100 text-green-800'
                              : vehicle.status === 'sold'
                                ? 'bg-blue-100 text-blue-800'
                                : vehicle.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : vehicle.status === 'wholesale'
                                    ? 'bg-orange-100 text-orange-800'
                                    : vehicle.status === 'repo'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {vehicle.status}
                        </span>
                      </td>
                      <td
                        className={`whitespace-nowrap text-right font-medium ${compactTableView ? 'px-3 py-2 text-xs' : 'px-6 py-4 text-sm'}`}
                      >
                        <Link
                          to={`/vehicle/${vehicle.vin}`}
                          className="text-blue-600 hover:text-blue-900 mr-2 font-medium"
                        >
                          View
                        </Link>
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => handleEditVehicle(vehicle)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-900 ml-2"
                          onClick={() => setFacebookPostVehicle(vehicle)}
                        >
                          Facebook Post
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {filteredVehicles.length} of {vehicles.length} vehicles
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sold Inventory Tab */}
      {activeTab === 'sold' && (
        <div className="card">
          {(() => {
            const soldVehicles = vehicles.filter((v) => v.status?.toLowerCase() === 'sold');
            if (soldVehicles.length === 0) {
              return (
                <div className="text-center py-12">
                  <ArchiveBoxIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Sold Vehicles</h3>
                  <p className="text-gray-500">
                    Vehicles will appear here once they are marked as sold.
                  </p>
                </div>
              );
            }
            return (
              <div className="overflow-x-auto">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">
                    Sold Inventory ({soldVehicles.length})
                  </h3>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Stock #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Vehicle
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        VIN
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Sale Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Purchase Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Profit
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Sold Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {soldVehicles.map((v) => {
                      const salePrice = v.sale_price || v.selling_price || 0;
                      const purchasePrice = v.purchase_price || 0;
                      const profit = salePrice - purchasePrice;
                      return (
                        <tr
                          key={v.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/vehicles/${v.id}`)}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {v.stock_number || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {v.year} {v.make} {v.model}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">
                            {v.vin}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">
                            ${salePrice.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            ${purchasePrice.toLocaleString()}
                          </td>
                          <td
                            className={`px-4 py-3 text-sm font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                          >
                            ${profit.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {v.sold_date
                              ? new Date(v.sold_date).toLocaleDateString()
                              : v.updated_at
                                ? new Date(v.updated_at).toLocaleDateString()
                                : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* NEW DASHBOARD TABS - Enhanced Functionality */}

      {/* Pricing Summary Tab */}
      {activeTab === 'pricing' && (
        <div className="space-y-6">
          {/* Interactive Pricing Charts */}
          <PricingTrendCharts pricingData={pricingSummary} loading={dashboardLoading} />

          {dashboardLoading ? (
            <div className="text-center py-12">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading pricing data...</p>
            </div>
          ) : pricingSummary && pricingSummary.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {pricingSummary.map((vehicle) => (
                <PricingSummaryCardCompact
                  key={vehicle.vehicle_id}
                  vehicle={vehicle}
                  onAdjustPrice={handleAdjustPrice}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Pricing Data Available</h3>
              <p className="text-gray-500 mb-4">Unable to load vehicle pricing information.</p>
              <button onClick={() => fetchDashboardData('pricing')} className="btn-secondary">
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stocking Guide Tab */}
      {activeTab === 'stocking' && (
        <StockingGuideDashboard
          data={inventoryMetrics}
          loading={dashboardLoading}
          onRefresh={() => fetchDashboardData('stocking')}
          dateRange={stockingGuideDateRange}
          onDateRangeChange={setStockingGuideDateRange}
        />
      )}

      {/* Online Marketing Tab */}
      {activeTab === 'marketing' && (
        <div className="space-y-6">
          {dashboardLoading ? (
            <div className="text-center py-12">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading marketing data...</p>
            </div>
          ) : marketingData && marketingData.length > 0 ? (
            <MarketingSyndicator vehicles={marketingData} onSyndicate={handleSyndicate} />
          ) : (
            <div className="text-center py-12">
              <GlobeAltIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Marketing Data Available
              </h3>
              <p className="text-gray-500 mb-4">
                Unable to load marketing syndication information.
              </p>
              <button onClick={() => fetchDashboardData('marketing')} className="btn-secondary">
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sales & Profitability Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {dashboardLoading ? (
            <div className="text-center py-12">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Loading analytics...</p>
            </div>
          ) : salesMetrics ? (
            <SalesAnalyticsCard
              metrics={salesMetrics}
              onPeriodChange={handleAnalyticsPeriodChange}
            />
          ) : (
            <div className="text-center py-12">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Sales Data Available</h3>
              <p className="text-gray-500 mb-4">
                Unable to load sales and profitability information.
              </p>
              <button onClick={() => fetchDashboardData('analytics')} className="btn-secondary">
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* CarsForSale FTP Import Configuration Tab */}
      {activeTab === 'ftp-import' && (
        <div>
          <CarsForSaleImportConfig />
        </div>
      )}

      {/* PRESERVED: Original Vehicle Modal with QR/VIN Decoder */}
      <VehicleModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedVehicle(null);
        }}
        onSave={handleSaveVehicle}
        vehicle={selectedVehicle}
        loading={actionLoading}
      />

      {facebookPostVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Facebook Business Page Post</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {facebookPostVehicle.year} {facebookPostVehicle.make} {facebookPostVehicle.model}
                </p>
              </div>
              <button
                onClick={handleCloseFacebookPost}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-xs text-blue-700 mt-3">
              Facebook Marketplace doesn’t allow business posting. Generate a ready-to-post update
              for your business page.
            </p>

            <div className="mt-4 space-y-2">
              <label className="block text-xs font-medium text-gray-700">
                Business Page URL (optional)
              </label>
              <input
                type="url"
                value={facebookPageUrl}
                onChange={(event) => setFacebookPageUrl(event.target.value)}
                placeholder="https://www.facebook.com/YourBusinessPage"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={handleGenerateFacebookPost}
                disabled={facebookPostLoading}
                className="btn-primary"
              >
                {facebookPostLoading ? 'Generating...' : 'Generate Page Post'}
              </button>
              {facebookPageUrl && (
                <a
                  href={facebookPageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary"
                >
                  Open Page
                </a>
              )}
            </div>

            {facebookPostError && <p className="mt-3 text-xs text-red-600">{facebookPostError}</p>}

            {facebookPost && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">Post Text</span>
                  <button
                    onClick={handleCopyFacebookPost}
                    className="text-xs text-blue-700 hover:underline"
                  >
                    Copy
                  </button>
                </div>
                <textarea
                  readOnly
                  value={facebookPost.post_text}
                  rows={5}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs"
                />
                {facebookPost.images.length > 0 && (
                  <div className="text-xs text-blue-800">
                    {facebookPost.images.length} image(s) ready to attach.
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4">
              <h4 className="text-sm font-semibold text-green-900">Auto-post to Connected Page</h4>
              <p className="text-xs text-green-700 mt-1">
                Once connected via OAuth, SimpleDealer can publish this listing directly to your
                Facebook business page.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={handleAutoPostToFacebook}
                  disabled={facebookAutoPostLoading}
                  className="px-3 py-2 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {facebookAutoPostLoading ? 'Posting...' : 'Post to Facebook Page'}
                </button>
              </div>
              {facebookAutoPostError && (
                <p className="mt-2 text-xs text-red-600">{facebookAutoPostError}</p>
              )}
              {facebookAutoPost && (
                <div className="mt-2 text-xs text-green-800">
                  <p>Status: {facebookAutoPost.status}</p>
                  {facebookAutoPost.post_url && (
                    <a
                      href={facebookAutoPost.post_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-green-700 hover:underline"
                    >
                      View Facebook Post
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button onClick={handleCloseFacebookPost} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {vehicleToAdjust && (
        <PriceAdjustmentModal
          isOpen={priceAdjustmentModalOpen}
          onClose={() => {
            setPriceAdjustmentModalOpen(false);
            setVehicleToAdjust(null);
          }}
          vehicle={vehicleToAdjust}
          onSave={handlePriceUpdate}
          loading={actionLoading}
        />
      )}

      {/* Advanced Filters Modal */}
      <AdvancedFilters
        isOpen={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        currentFilters={activeFilters}
        availableMakes={availableMakes}
        availableModels={availableModels}
        availableStatuses={availableStatuses}
        availableLocations={availableLocations}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        data={
          exportDataType === 'vehicles'
            ? filteredVehicles
            : exportDataType === 'pricing'
              ? pricingSummary
              : salesMetrics
                ? [salesMetrics]
                : []
        }
        dataType={exportDataType}
        onExport={handleExport}
        loading={actionLoading}
      />
    </div>
  );
};

export default VehiclesPage;
